import axios from "axios";
import { ACCESS_TOKEN_REVOKE_PATH } from "../Api/Plugin/Auth/revokeAccessToken";
import { useConfigStore } from "../ConfigStore/useConfigStore";
import { useAccountsStore } from "./useAccountsStore";

const REQUEST_TIMEOUT_MS = 5000;
const SESSION_REVOKE_PATH = "/api/users/me/sessions/current/revoke";

/**
 * Ends the server-side session of an account slot this tab is not working in. The slot only holds a
 * refresh token, so it is traded for an access token first; that bearer is then used to revoke the
 * session and itself. Best effort by design: the caller removes the slot whatever happens, and a
 * failure only means the session dies at its expiry instead of now.
 *
 * It does not go through the shared `api` instance, whose interceptors would replace the bearer
 * with the token of the account this tab works in.
 */
export async function releaseAccountSlot(index: number): Promise<void> {
  const refreshToken = useAccountsStore().readRefreshToken(index);
  const config = useConfigStore().config.value;
  if (!refreshToken || !config) {
    return;
  }

  try {
    const grant = await axios.post<{ access_token?: string }>(`${config.loginUrl}/oauth/v2/token`, {
      grant_type: "refresh_token",
      refresh_token: refreshToken,
      client_id: `${config.loginClientId}`,
      redirect_uri: `${config.loginRedirect}/oauth/v2/callback`,
    }, { timeout: REQUEST_TIMEOUT_MS });

    const accessToken = grant.data.access_token;
    if (!accessToken) {
      return;
    }
    const request = { timeout: REQUEST_TIMEOUT_MS, headers: { "Authorization": `Bearer ${accessToken}`, "Content-Type": "application/ld+json" } };
    // The session goes first: revoking the access token would reject the bearer of the second call.
    await axios.post(`${config.apiUrl}${SESSION_REVOKE_PATH}`, undefined, request);
    await axios.post(`${config.apiUrl}${ACCESS_TOKEN_REVOKE_PATH}`, undefined, request);
  } catch (error) {
    console.warn(`[Accounts] The session of slot ${index} could not be revoked, it stays valid until it expires.`, error);
  }
}
